import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

/****
 * AdminMilestoneTrigger
 *
 * Purpose: One-off admin utility to manually trigger a milestone email via URL params.
 * Usage (admin only):
 *   Add to the URL: ?trigger_milestone_for=Emeka%20Ibe&milestone=10&ctx=6951EAA2-1264-4A1A-A86D-817E462202C7
 * Safeguards:
 *  - Requires logged-in admin
 *  - Requires a context token match
 *  - Debounced by localStorage to avoid duplicates per user+milestone
 */
export default function AdminMilestoneTrigger() {
  const { userRole } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;

    const params = new URLSearchParams(location.search);
    const targetName = params.get("trigger_milestone_for");
    const milestoneParam = params.get("milestone") || "10";
    const ctx = params.get("ctx");

    if (!targetName || !ctx) return;
    if (userRole !== "admin") return;

    // Require the context token provided by the user to avoid accidental triggers
    if (ctx !== "6951EAA2-1264-4A1A-A86D-817E462202C7") return;

    ranRef.current = true;

    const milestone = parseInt(milestoneParam, 10) || 10;
    const dedupeKey = `milestone_triggered_${targetName}_${milestone}`;
    if (localStorage.getItem(dedupeKey)) return;

    (async () => {
      try {
        console.info(
          "[AdminMilestoneTrigger] Resolving user by display_name:",
          targetName,
        );
        const { data: profile, error: profileError } = await supabase
          .from("public_profiles")
          .select("id, display_name")
          .eq("display_name", targetName)
          .order("created_at", { ascending: false })
          .maybeSingle();

        if (profileError || !profile?.id) {
          console.error(
            "[AdminMilestoneTrigger] Profile lookup failed:",
            profileError,
          );
          toast({
            title: "Milestone Trigger Failed",
            description: `Could not find artist: ${targetName}`,
            variant: "destructive",
          });
          return;
        }

        console.info(
          "[AdminMilestoneTrigger] Invoking milestone email function...",
          {
            artist_id: profile.id,
            milestone,
          },
        );

        const { data, error } = await supabase.functions.invoke(
          "send-streams-milestone-notification",
          {
            body: {
              artist_id: profile.id,
              milestone,
            },
          },
        );

        if (error) {
          console.error(
            "[AdminMilestoneTrigger] Function invoke error:",
            error,
          );
          toast({
            title: "Milestone Email Error",
            description: error.message ?? "Unknown error",
            variant: "destructive",
          });
          return;
        }

        localStorage.setItem(dedupeKey, "1");

        console.info("[AdminMilestoneTrigger] Success:", data);
        toast({
          title: "Milestone Email Sent",
          description: `Triggered ${milestone}-streams email for ${targetName}`,
          variant: "default",
        });
      } catch (err: any) {
        console.error("[AdminMilestoneTrigger] Unexpected error:", err);
        toast({
          title: "Milestone Trigger Failed",
          description: err?.message ?? "Unexpected error",
          variant: "destructive",
        });
      }
    })();
  }, [location.search, userRole, toast]);

  return null;
}
